import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radar, Play, Square, Download, Zap } from "lucide-react";
import { toast } from "sonner";
import { ProspectsTable } from "./ProspectsTable";
import { MarketPicker } from "./MarketPicker";
import { AnnounceBuyerFinder } from "./AnnounceBuyerFinder";

const inputCls = "h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#FF3B30]";
const chip = (on) => `px-3 h-8 rounded-full text-xs font-bold border transition-colors ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#27272A] text-neutral-400 hover:border-neutral-500"}`;

export default function VisibilityIntelligence() {
  const [opts, setOpts] = useState(null);
  const [runs, setRuns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [kind, setKind] = useState("sellers");
  const [markets, setMarkets] = useState(["ng"]);
  const [topics, setTopics] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeRun, setActiveRun] = useState("");

  const load = useCallback(async () => {
    try {
      const [o, r, s] = await Promise.all([api.get("/discovery/options"), api.get("/discovery/runs"), api.get("/discovery/settings")]);
      setOpts(o.data); setRuns(r.data.items); setSettings(s.data);
    } catch { setOpts({}); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = runs.some((r) => r.status === "running" || r.status === "queued");
  useEffect(() => { if (!running) return; const t = setInterval(() => api.get("/discovery/runs").then((r) => setRuns(r.data.items)).catch(() => {}), 5000); return () => clearInterval(t); }, [running]);

  const start = async () => {
    setBusy(true);
    try {
      const body = { kind, markets, topics: topics.split(",").map((t) => t.trim()).filter(Boolean), name: name.trim() || undefined };
      const { data } = await api.post("/discovery/runs", body);
      toast.success(`Discovery started: ${data.name}`); setActiveRun(data.id); setName(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); } finally { setBusy(false); }
  };
  const saveSettings = async (patch) => {
    try { const { data } = await api.put("/discovery/settings", patch); setSettings(data); toast.success("Saved"); } catch { toast.error("Could not save"); }
  };

  if (!opts) return null;
  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="visibility-intelligence-card">
      <div className="flex items-center gap-2">
        <Radar className="h-5 w-5 text-[#FF3B30]" />
        <h2 className="font-black text-xl tracking-tight">Visibility Intelligence</h2>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30]">{running ? "scanning" : "ready"}</span>
      </div>
      <p className="text-xs text-neutral-500 mt-1 max-w-3xl">
        Scans public search results, websites, public social pages and marketplaces in every country on earth for sellers & creators who should be on VISMARKS — and for communities where our products can be promoted. Pick a region, the whole world, or search any country.
        AI scores each find and drafts a personal invite. Only publicly published information is used; every email carries a one-tap unsubscribe.
      </p>

      <div className="mt-5 grid lg:grid-cols-[1fr_auto] gap-4 items-end">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500 w-16">Find</span>
            <button onClick={() => setKind("sellers")} className={chip(kind === "sellers")} data-testid="vi-kind-sellers">Sellers & creators</button>
            <button onClick={() => setKind("communities")} className={chip(kind === "communities")} data-testid="vi-kind-communities">Buyer communities</button>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs text-neutral-500 w-16 pt-2">Markets</span>
            <div className="flex-1"><MarketPicker regions={opts.regions} countries={opts.countries} value={markets} onChange={setMarkets} testPrefix="vi-market" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500 w-16">Focus</span>
            <input value={topics} onChange={(e) => setTopics(e.target.value)} data-testid="vi-topics-input" className={`${inputCls} flex-1 min-w-[260px]`}
              placeholder={kind === "sellers" ? "optional niches, comma-separated — e.g. fashion designer, wig vendor, cake baker" : "optional category codes — e.g. fashion, beauty, home"} />
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="vi-name-input" className={`${inputCls} w-56`} placeholder="Run name (optional)" />
          </div>
        </div>
        <button onClick={start} disabled={busy || !markets.length} data-testid="vi-start-btn"
          className="h-12 px-6 inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white font-bold hover:bg-[#e0342a] transition-colors disabled:opacity-50"><Play className="h-4 w-4" /> Start discovery</button>
      </div>

      {settings && (
        <div className="mt-5 rounded-lg border border-[#27272A] bg-[#0A0A0A] p-4 flex flex-wrap items-center gap-4" data-testid="vi-autosend-box">
          <Zap className="h-4 w-4 text-[#FFD60A]" />
          <label className="flex items-center gap-2 text-sm font-bold text-white cursor-pointer">
            <input type="checkbox" checked={settings.auto_send} onChange={(e) => saveSettings({ auto_send: e.target.checked })} data-testid="vi-autosend-toggle" className="accent-[#FF3B30]" /> Auto-send invites to qualified sellers
          </label>
          <span className="text-xs text-neutral-500">score ≥</span>
          <input type="number" min={50} max={100} defaultValue={settings.min_score} onBlur={(e) => saveSettings({ min_score: Number(e.target.value) })} data-testid="vi-minscore-input" className={`${inputCls} w-20 text-center`} />
          <span className="text-xs text-neutral-500">· up to</span>
          <input type="number" min={1} max={200} defaultValue={settings.daily_cap} onBlur={(e) => saveSettings({ daily_cap: Number(e.target.value) })} data-testid="vi-cap-input" className={`${inputCls} w-20 text-center`} />
          <span className="text-xs text-neutral-500">emails a day · sent today: <span className="text-white font-mono" data-testid="vi-sent-today">{settings.sent_today}</span></span>
          <span className="ml-auto text-[11px] text-neutral-600">Off = you review each prospect and send with one tap below.</span>
        </div>
      )}

      {runs.length > 0 && (
        <div className="mt-5 overflow-x-auto" data-testid="vi-runs">
          <table className="w-full text-xs">
            <thead><tr className="text-neutral-500 text-left border-b border-[#27272A]"><th className="py-2 pr-3">Run</th><th className="pr-3">Status</th><th className="pr-3">Progress</th><th className="pr-3">Found</th><th className="pr-3">Qualified</th><th className="pr-3">Started</th><th></th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className={`border-b border-[#1c1c1c] cursor-pointer hover:bg-white/[0.02] ${activeRun === r.id ? "bg-white/[0.04]" : ""}`} onClick={() => setActiveRun(activeRun === r.id ? "" : r.id)} data-testid={`vi-run-${r.id}`}>
                  <td className="py-2 pr-3 text-white font-bold">{r.name}</td>
                  <td className="pr-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${r.status === "done" ? "text-[#34C759] border-[#34C759]/40" : r.status === "running" ? "text-[#FFD60A] border-[#FFD60A]/40" : "text-neutral-500 border-white/10"}`}>{r.status}</span></td>
                  <td className="pr-3 font-mono text-neutral-400">{r.done_queries}/{r.total_queries} searches</td>
                  <td className="pr-3 font-mono text-white">{r.found}</td>
                  <td className="pr-3 font-mono text-[#34C759]">{r.qualified}</td>
                  <td className="pr-3 text-neutral-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {(r.status === "running" || r.status === "queued") && <button onClick={() => api.post(`/discovery/runs/${r.id}/cancel`).then(load)} data-testid={`vi-cancel-${r.id}`} className="inline-flex items-center gap-1 text-neutral-400 hover:text-[#FF3B30]"><Square className="h-3 w-3" /> Stop</button>}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/discovery/prospects/export.csv?run_id=${r.id}`} className="ml-3 inline-flex items-center gap-1 text-neutral-400 hover:text-white" data-testid={`vi-export-${r.id}`}><Download className="h-3 w-3" /> CSV</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProspectsTable runId={activeRun} refreshKey={runs.map((r) => r.scored).join(",")} />
      <AnnounceBuyerFinder />
    </div>
  );
}
